import { PlainDate } from "@sa-nu/shared-utils/date";

import {
  type DatePickerSelection,
  isValidEndDateCandidate,
} from "./get-next-selection";

export type DateCellVariant =
  | "normal"
  | "today"
  | "disabled"
  // 土日祝が disabled のときの専用 state (グレーで色味が消えるのを防ぐ)。色味はデザイナー確認待ち。
  | "disabledHoliday"
  | "todayDisabled"
  | "selected"
  | "inRange";

export type SelectionContinuity = "none" | "before" | "after" | "both";

/** disabled 時の見た目。"muted" (default) = グレーアウト / "normal" = 通常表示のまま非インタラクティブ */
export type DisabledAppearance = "muted" | "normal";

export type DateCellState = {
  variant: DateCellVariant;
  continuity: SelectionContinuity;
  isInteractive: boolean;
};

export function deriveDateCellState(params: {
  date: PlainDate;
  selection: DatePickerSelection;
  today: PlainDate;
  baseDisabled: boolean;
  /** baseDisabled 時の見た目。省略時は "muted" (従来のグレーアウト) */
  disabledAppearance?: DisabledAppearance;
  maxNights: number | undefined;
  /** 土曜・日曜・祝日のとき true。disabled 時に専用 variant へ振り分けるのに使う (省略時 false) */
  isHoliday?: boolean;
}): DateCellState {
  const {
    date,
    selection,
    today,
    baseDisabled,
    disabledAppearance = "muted",
    maxNights,
    isHoliday,
  } = params;
  const { startDate, endDate } = selection;

  // Rule 1: date equals startDate
  if (startDate !== null && date.equals(startDate)) {
    return {
      variant: "selected",
      continuity: endDate !== null ? "after" : "none",
      isInteractive: true,
    };
  }

  // Rule 2: date equals endDate
  if (endDate !== null && date.equals(endDate)) {
    return { variant: "selected", continuity: "before", isInteractive: true };
  }

  // Rule 3: startDate < date < endDate (in-range)
  if (
    startDate !== null &&
    endDate !== null &&
    PlainDate.compare(date, startDate) > 0 &&
    PlainDate.compare(date, endDate) < 0
  ) {
    return { variant: "inRange", continuity: "both", isInteractive: true };
  }

  // Rule 4: selectingEnd phase — dates outside valid endDate window appear disabled
  // but remain interactive (so a click fires the cancel logic).
  // 窓外表現は disabledAppearance に関係なく muted 系 (土日祝は色味を残す disabledHoliday)
  const isSelectingEnd =
    startDate !== null && endDate === null && maxNights !== undefined;

  if (isSelectingEnd) {
    const isValidCandidate = isValidEndDateCandidate({
      startDate,
      candidate: date,
      candidateDisabled: baseDisabled,
      maxNights,
    });

    if (!isValidCandidate) {
      const variant = date.equals(today)
        ? "todayDisabled"
        : isHoliday
          ? "disabledHoliday"
          : "disabled";
      return { variant, continuity: "none", isInteractive: true };
    }
  }

  // Rule 5: baseDisabled (outside selectingEnd phase, or valid candidate)
  if (baseDisabled) {
    // disabledAppearance "normal": 見た目は通常 (today ならオレンジ) のまま非インタラクティブ
    if (disabledAppearance === "normal") {
      const variant = date.equals(today) ? "today" : "normal";
      return { variant, continuity: "none", isInteractive: false };
    }
    const variant = date.equals(today)
      ? "todayDisabled"
      : isHoliday
        ? "disabledHoliday"
        : "disabled";
    return { variant, continuity: "none", isInteractive: false };
  }

  // Rule 6: today
  if (date.equals(today)) {
    return { variant: "today", continuity: "none", isInteractive: true };
  }

  // Rule 7: normal
  return { variant: "normal", continuity: "none", isInteractive: true };
}
